import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Timestamp, addDoc } from 'firebase/firestore'
import {
    midMileShipmentsCollection,
    contractorsCollection,
    midMileRateTablesCollection,
    midMileTariffsCollection,
    midMileAuctionsCollection,
    midMileBidsCollection,
    getCountForCollection,
    getCountForQuery,
    query,
    where
} from '../../../../services/firebase/firebaseConfig'
import {
    MidMileShipment,
    Contractor,
    MidMileRateTable,
    MidMileTariff,
    MidMileAuction,
    Bid,
    HubLocation,
    TransitLane,
    ShipmentStatus,
    CarrierStatus,
    RateTableStatus,
    TariffStatus,
    AuctionStatus,
    MIDMILE_COLLECTIONS,
    VerificationStatus
} from '../../../../types/midmile.types'

// ============================================================================
// STATE INTERFACES
// ============================================================================

interface MidMileState {
    // Shipments
    shipments: MidMileShipment[]
    selectedShipment: MidMileShipment | null
    shipmentsLoading: boolean
    shipmentsError: string | null
    
    // Carriers
    carriers: Contractor[]
    selectedCarrier: Contractor | null
    carriersLoading: boolean
    carriersError: string | null
    
    // Rate Tables
    rateTables: MidMileRateTable[]
    selectedRateTable: MidMileRateTable | null
    rateTablesLoading: boolean
    rateTablesError: string | null
    
    // Tariffs
    tariffs: MidMileTariff[]
    selectedTariff: MidMileTariff | null
    tariffsLoading: boolean
    tariffsError: string | null
    
    // Auctions
    auctions: MidMileAuction[]
    selectedAuction: MidMileAuction | null
    auctionsLoading: boolean
    auctionsError: string | null
    
    // Bids
    bids: Bid[]
    bidsLoading: boolean
    bidsError: string | null
    
    // Hubs & Lanes
    hubs: HubLocation[]
    lanes: TransitLane[]
    hubsLoading: boolean
    lanesLoading: boolean
    hubsError: string | null
    lanesError: string | null
    
    // Statistics
    stats: {
        totalShipments: number
        activeCarriers: number
        rateTables: number
        activeTariffs: number
        openAuctions: number
        totalBids: number
    }
    
    // UI State
    filters: {
        shipmentStatus: ShipmentStatus | 'all'
        carrierStatus: CarrierStatus | 'all'
        dateRange: { start: Date | null; end: Date | null }
        searchTerm: string
    }
    
    // General
    dataLoaded: boolean
    error: string | null
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: MidMileState = {
    // Shipments
    shipments: [],
    selectedShipment: null,
    shipmentsLoading: false,
    shipmentsError: null,
    
    // Carriers
    carriers: [],
    selectedCarrier: null,
    carriersLoading: false,
    carriersError: null,
    
    // Rate Tables
    rateTables: [],
    selectedRateTable: null,
    rateTablesLoading: false,
    rateTablesError: null,
    
    // Tariffs
    tariffs: [],
    selectedTariff: null,
    tariffsLoading: false,
    tariffsError: null,
    
    // Auctions
    auctions: [],
    selectedAuction: null,
    auctionsLoading: false,
    auctionsError: null,
    
    // Bids
    bids: [],
    bidsLoading: false,
    bidsError: null,
    
    // Hubs & Lanes
    hubs: [],
    lanes: [],
    hubsLoading: false,
    lanesLoading: false,
    hubsError: null,
    lanesError: null,
    
    // Statistics
    stats: {
        totalShipments: 0,
        activeCarriers: 0,
        rateTables: 0,
        activeTariffs: 0,
        openAuctions: 0,
        totalBids: 0
    },
    
    // UI State
    filters: {
        shipmentStatus: 'all',
        carrierStatus: 'all',
        dateRange: { start: null, end: null },
        searchTerm: ''
    },
    
    // General
    dataLoaded: false,
    error: null
}

// ============================================================================
// ASYNC THUNKS
// ============================================================================

// Shipment Management
export const fetchShipments = createAsyncThunk(
    'midMile/fetchShipments',
    async (_, { rejectWithValue }) => {
        try {
            const shipmentsSnapshot = await midMileShipmentsCollection.get()
            if (!shipmentsSnapshot) return []
            
            const shipments = shipmentsSnapshot.docs.map((doc: any) => ({ 
                id: doc.id, 
                ...doc.data() 
            }))
            return shipments || []
        } catch (error) {
            return rejectWithValue('Failed to fetch shipments')
        }
    }
)

export const createShipment = createAsyncThunk(
    'midMile/createShipment',
    async (shipmentData: Omit<MidMileShipment, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
        try {
            const shipmentWithTimestamps = {
                ...shipmentData,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            }
            // Use addDoc directly to get the proper document ID
            const docRef = await addDoc(midMileShipmentsCollection.collectionInstance, shipmentWithTimestamps)
            return { ...shipmentWithTimestamps, id: docRef.id }
        } catch (error) {
            return rejectWithValue('Failed to create shipment')
        }
    }
)

export const updateShipmentStatus = createAsyncThunk(
    'midMile/updateShipmentStatus',
    async ({ shipmentId, status, additionalData }: { shipmentId: string; status: ShipmentStatus; additionalData?: any }, { rejectWithValue }) => {
        try {
            const updateData = {
                status,
                updatedAt: Timestamp.now(),
                ...additionalData
            }
            const docRef = await midMileShipmentsCollection.doc(shipmentId)
            if ('update' in docRef) {
                await docRef.update(updateData)
            }
            return { id: shipmentId, status, ...additionalData }
        } catch (error) {
            return rejectWithValue('Failed to update shipment status')
        }
    }
)

// Carrier Management
export const fetchCarriers = createAsyncThunk(
    'midMile/fetchCarriers',
    async (_, { rejectWithValue }) => {
        try {
            const carriersSnapshot = await contractorsCollection.get()
            if (!carriersSnapshot) return []
            
            const allContractors = carriersSnapshot.docs.map((doc: any) => ({ 
                id: doc.id, 
                ...doc.data() 
            }))
            
            console.log('=== FETCH CARRIERS DEBUG ===')
            console.log('Total contractors fetched:', allContractors.length)
            console.log('Contractors with isMidMileCarrier flag:', allContractors.filter((c: any) => c.isMidMileCarrier).length)
            console.log('All contractors:', allContractors.map((c: any) => ({ id: c.id, companyName: c.companyName, isMidMileCarrier: c.isMidMileCarrier })))
            
            const carriers = allContractors.filter((carrier: any) => carrier.isMidMileCarrier) // Only return mid-mile carriers
            console.log('Filtered mid-mile carriers:', carriers.map((c: any) => ({ id: c.id, companyName: c.companyName })))
            
            return carriers || []
        } catch (error) {
            console.error('Error fetching carriers:', error)
            return rejectWithValue('Failed to fetch carriers')
        }
    }
)

export const createCarrier = createAsyncThunk(
    'midMile/createCarrier',
    async (carrierData: Omit<Contractor, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
        try {
            const carrierWithTimestamps = {
                ...carrierData,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            }
            // Use addDoc directly to get the proper document ID
            const docRef = await addDoc(contractorsCollection.collectionInstance, carrierWithTimestamps)
            return { ...carrierWithTimestamps, id: docRef.id }
        } catch (error) {
            return rejectWithValue('Failed to create carrier')
        }
    }
)

export const updateCarrierStatus = createAsyncThunk(
    'midMile/updateCarrierStatus',
    async ({ carrierId, status, additionalData }: { carrierId: string; status: CarrierStatus; additionalData?: any }, { rejectWithValue }) => {
        try {
            const updateData = {
                'midMile.status': status,
                updatedAt: Timestamp.now(),
                ...additionalData
            }
            const docRef = await contractorsCollection.doc(carrierId)
            if ('update' in docRef) {
                await docRef.update(updateData)
            }
            return { id: carrierId, midMile: { status }, ...additionalData }
        } catch (error) {
            return rejectWithValue('Failed to update carrier status')
        }
    }
)

export const updateCarrierRating = createAsyncThunk(
    'midMile/updateCarrierRating',
    async ({ carrierId, rating, onTimePercentage, damageRate }: { carrierId: string; rating: number; onTimePercentage: number; damageRate: number }, { rejectWithValue }) => {
        try {
            const updateData = {
                'midMile.rating': rating,
                'midMile.onTimePercentage': onTimePercentage,
                'midMile.damageRate': damageRate,
                updatedAt: Timestamp.now()
            }
            const docRef = await contractorsCollection.doc(carrierId)
            if ('update' in docRef) {
                await docRef.update(updateData)
            }
            return { 
                id: carrierId, 
                midMile: { 
                    rating, 
                    onTimePercentage, 
                    damageRate,
                    mcNumber: '',
                    dotNumber: '',
                    status: CarrierStatus.ACTIVE,
                    verification: VerificationStatus.PENDING,
                    equipment: [],
                    serviceAreas: [],
                    certifications: [],
                    totalShipments: 0,
                    paymentTerms: 0,
                    insurance: {
                        provider: '',
                        policyNumber: '',
                        coverage: 0,
                        expiryDate: Timestamp.now()
                    },
                    creditLimit: 0,
                    autoBid: false,
                    minBidAmount: 0,
                    preferredLanes: []
                } 
            }
        } catch (error) {
            return rejectWithValue('Failed to update carrier rating')
        }
    }
)

// Rate Tables
export const fetchRateTables = createAsyncThunk(
    'midMile/fetchRateTables',
    async (_, { rejectWithValue }) => {
        try {
            const rateTables = await contractorsCollection.get()
            return rateTables || []
        } catch (error) {
            return rejectWithValue('Failed to fetch rate tables')
        }
    }
)

// Tariffs
export const fetchTariffs = createAsyncThunk(
    'midMile/fetchTariffs',
    async (_, { rejectWithValue }) => {
        try {
            const tariffs = await contractorsCollection.get()
            return tariffs || []
        } catch (error) {
            return rejectWithValue('Failed to fetch tariffs')
        }
    }
)

// Auctions
export const fetchAuctions = createAsyncThunk(
    'midMile/fetchAuctions',
    async (_, { rejectWithValue }) => {
        try {
            const auctions = await contractorsCollection.get()
            return auctions || []
        } catch (error) {
            return rejectWithValue('Failed to fetch auctions')
        }
    }
)

// Bids
export const fetchBids = createAsyncThunk(
    'midMile/fetchBids',
    async (_, { rejectWithValue }) => {
        try {
            const bids = await contractorsCollection.get()
            return bids || []
        } catch (error) {
            return rejectWithValue('Failed to fetch bids')
        }
    }
)

// Hubs & Lanes
export const fetchHubs = createAsyncThunk(
    'midMile/fetchHubs',
    async (_, { rejectWithValue }) => {
        try {
            const hubs = await contractorsCollection.get()
            return hubs || []
        } catch (error) {
            return rejectWithValue('Failed to fetch hubs')
        }
    }
)

export const fetchLanes = createAsyncThunk(
    'midMile/fetchLanes',
    async (_, { rejectWithValue }) => {
        try {
            const lanes = await contractorsCollection.get()
            return lanes || []
        } catch (error) {
            return rejectWithValue('Failed to fetch lanes')
        }
    }
)

// Statistics
export const fetchStatistics = createAsyncThunk(
    'midMile/fetchStatistics',
    async (_, { rejectWithValue }) => {
        try {
            const [
                totalShipments,
                activeCarriers,
                rateTables,
                activeTariffs,
                openAuctions,
                totalBids
            ] = await Promise.all([
                getCountForCollection(midMileShipmentsCollection.collectionInstance),
                getCountForQuery(
                    query(
                        contractorsCollection.collectionInstance,
                        where('isMidMileCarrier', '==', true),
                        where('active', '==', true)
                    )
                ),
                getCountForCollection(midMileRateTablesCollection.collectionInstance),
                getCountForQuery(
                    query(
                        midMileTariffsCollection.collectionInstance,
                        where('status', '==', TariffStatus.ACTIVE)
                    )
                ),
                getCountForQuery(
                    query(
                        midMileAuctionsCollection.collectionInstance,
                        where('status', '==', AuctionStatus.OPEN)
                    )
                ),
                getCountForCollection(midMileBidsCollection.collectionInstance)
            ])
            
            return {
                totalShipments,
                activeCarriers,
                rateTables,
                activeTariffs,
                openAuctions,
                totalBids
            }
        } catch (error) {
            return rejectWithValue('Failed to fetch statistics')
        }
    }
)

// ============================================================================
// SLICE
// ============================================================================

const midMileSlice = createSlice({
    name: 'midMile',
    initialState,
    reducers: {
        // Shipment actions
        setSelectedShipment: (state, action) => {
            state.selectedShipment = action.payload
        },
        clearSelectedShipment: (state) => {
            state.selectedShipment = null
        },
        
        // Carrier actions
        setSelectedCarrier: (state, action) => {
            state.selectedCarrier = action.payload
        },
        clearSelectedCarrier: (state) => {
            state.selectedCarrier = null
        },
        
        // Rate Table actions
        setSelectedRateTable: (state, action) => {
            state.selectedRateTable = action.payload
        },
        clearSelectedRateTable: (state) => {
            state.selectedRateTable = null
        },
        
        // Tariff actions
        setSelectedTariff: (state, action) => {
            state.selectedTariff = action.payload
        },
        clearSelectedTariff: (state) => {
            state.selectedTariff = null
        },
        
        // Auction actions
        setSelectedAuction: (state, action) => {
            state.selectedAuction = action.payload
        },
        clearSelectedAuction: (state) => {
            state.selectedAuction = null
        },
        
        // Filter actions
        setShipmentStatusFilter: (state, action) => {
            state.filters.shipmentStatus = action.payload
        },
        setCarrierStatusFilter: (state, action) => {
            state.filters.carrierStatus = action.payload
        },
        setDateRangeFilter: (state, action) => {
            state.filters.dateRange = action.payload
        },
        setSearchTermFilter: (state, action) => {
            state.filters.searchTerm = action.payload
        },
        clearFilters: (state) => {
            state.filters = initialState.filters
        },
        
        // Error handling
        clearError: (state) => {
            state.error = null
        },
        clearAllErrors: (state) => {
            state.shipmentsError = null
            state.carriersError = null
            state.rateTablesError = null
            state.tariffsError = null
            state.auctionsError = null
            state.bidsError = null
            state.hubsError = null
            state.lanesError = null
            state.error = null
        }
    },
    extraReducers: (builder) => {
        // Shipments
        builder
            .addCase(fetchShipments.pending, (state) => {
                state.shipmentsLoading = true
                state.shipmentsError = null
            })
            .addCase(fetchShipments.fulfilled, (state, action) => {
                state.shipmentsLoading = false
                state.shipments = action.payload
            })
            .addCase(fetchShipments.rejected, (state, action) => {
                state.shipmentsLoading = false
                state.shipmentsError = action.payload as string
            })
            .addCase(createShipment.fulfilled, (state, action) => {
                state.shipments.unshift(action.payload)
            })
            .addCase(updateShipmentStatus.fulfilled, (state, action) => {
                const index = state.shipments.findIndex(s => s.id === action.payload.id)
                if (index !== -1) {
                    state.shipments[index] = { ...state.shipments[index], ...action.payload }
                }
            })
        
        // Carriers
        builder
            .addCase(fetchCarriers.pending, (state) => {
                state.carriersLoading = true
                state.carriersError = null
            })
            .addCase(fetchCarriers.fulfilled, (state, action) => {
                state.carriersLoading = false
                state.carriers = action.payload
            })
            .addCase(fetchCarriers.rejected, (state, action) => {
                state.carriersLoading = false
                state.carriersError = action.payload as string
            })
            .addCase(createCarrier.fulfilled, (state, action) => {
                state.carriers.unshift(action.payload)
            })
            .addCase(updateCarrierStatus.fulfilled, (state, action) => {
                const index = state.carriers.findIndex(c => c.id === action.payload.id)
                if (index !== -1) {
                    state.carriers[index] = { ...state.carriers[index], ...action.payload }
                }
            })
            .addCase(updateCarrierRating.fulfilled, (state, action) => {
                const index = state.carriers.findIndex(c => c.id === action.payload.id)
                if (index !== -1) {
                    state.carriers[index] = { ...state.carriers[index], ...action.payload }
                }
            })
        
        // Rate Tables
        builder
            .addCase(fetchRateTables.pending, (state) => {
                state.rateTablesLoading = true
                state.rateTablesError = null
            })
            .addCase(fetchRateTables.fulfilled, (state, action) => {
                state.rateTablesLoading = false
                state.rateTables = action.payload as any
            })
            .addCase(fetchRateTables.rejected, (state, action) => {
                state.rateTablesLoading = false
                state.error = action.payload as string
            })
        
        // Tariffs
        builder
            .addCase(fetchTariffs.pending, (state) => {
                state.tariffsLoading = true
                state.tariffsError = null
            })
            .addCase(fetchTariffs.fulfilled, (state, action) => {
                state.tariffsLoading = false
                state.tariffs = action.payload as any
            })
            .addCase(fetchTariffs.rejected, (state, action) => {
                state.tariffsLoading = false
                state.error = action.payload as string
            })
        
        // Auctions
        builder
            .addCase(fetchAuctions.pending, (state) => {
                state.auctionsLoading = true
                state.auctionsError = null
            })
            .addCase(fetchAuctions.fulfilled, (state, action) => {
                state.auctionsLoading = false
                state.auctions = action.payload as any
            })
            .addCase(fetchAuctions.rejected, (state, action) => {
                state.auctionsLoading = false
                state.error = action.payload as string
            })
        
        // Bids
        builder
            .addCase(fetchBids.pending, (state) => {
                state.bidsLoading = true
                state.bidsError = null
            })
            .addCase(fetchBids.fulfilled, (state, action) => {
                state.bidsLoading = false
                state.bids = action.payload as any
            })
            .addCase(fetchBids.rejected, (state, action) => {
                state.bidsLoading = false
                state.error = action.payload as string
            })
        
        // Hubs
        builder
            .addCase(fetchHubs.pending, (state) => {
                state.hubsLoading = true
                state.hubsError = null
            })
            .addCase(fetchHubs.fulfilled, (state, action) => {
                state.hubsLoading = false
                state.hubs = action.payload as any
            })
            .addCase(fetchHubs.rejected, (state, action) => {
                state.hubsLoading = false
                state.error = action.payload as string
            })
        
        // Lanes
        builder
            .addCase(fetchLanes.pending, (state) => {
                state.lanesLoading = true
                state.lanesError = null
            })
            .addCase(fetchLanes.fulfilled, (state, action) => {
                state.lanesLoading = false
                state.lanes = action.payload as any
            })
            .addCase(fetchLanes.rejected, (state, action) => {
                state.lanesLoading = false
                state.error = action.payload as string
            })
        
        // Statistics
        builder
            .addCase(fetchStatistics.fulfilled, (state, action) => {
                state.stats = action.payload
                state.dataLoaded = true
            })
    }
})

// ============================================================================
// EXPORTS
// ============================================================================

export const {
    setSelectedShipment,
    clearSelectedShipment,
    setSelectedCarrier,
    clearSelectedCarrier,
    setSelectedRateTable,
    clearSelectedRateTable,
    setSelectedTariff,
    clearSelectedTariff,
    setSelectedAuction,
    clearSelectedAuction,
    setShipmentStatusFilter,
    setCarrierStatusFilter,
    setDateRangeFilter,
    setSearchTermFilter,
    clearFilters,
    clearError,
    clearAllErrors
} = midMileSlice.actions

export default midMileSlice.reducer 